import React from 'react';

import { URL_SHORTENER_LINK_DOMAIN } from '../../shortened-urls/config/shortened-url-config';
import { CkEditorNewlineTrimmer } from '../../util/ckeditor-newline-trimmer';

type InputProps = {
  messageBody: string;
  includesHTML?: boolean;
  shouldTrim?: boolean;
  className?: string;
  urlVisitDetails?: { [pathLowerCased: string]: VisitDetail };
  dataCy?: string;
};

// Requires http(s):// or www.
// Followed by a non-greedy amount of any characters
// Until we hit a </(non-greedy any char)> (closing html tag), <(non-greedy any char)/> (opening html tag),
// white space, or the end of the string
const URL_PATTERN = /(https?:\/\/|www\.).+?(?=(<\/.+?>|<.+?\/>|\s|$))/gi;

const SHORTENED_URL_PATTERN = new RegExp(`${URL_SHORTENER_LINK_DOMAIN}/(\\S+)`, 'i');

const formatUrl = (url: string) => {
  const urlWithProtocol = url.match(/^http/) ? url : `https://${url}`;
  const urlWithoutTrailingPeriod =
    urlWithProtocol.slice(-1) === '.' ? urlWithProtocol.slice(0, -1) : urlWithProtocol;
  return urlWithoutTrailingPeriod;
};

export const FormattedMessageBody = ({
  messageBody,
  includesHTML,
  shouldTrim,
  className,
  urlVisitDetails,
  dataCy,
}: InputProps) => {
  const wrapUrlLink = (match: string) => {
    const url = formatUrl(match);
    let link = `<a class="typo-link-secondary" href="${url}" target="_blank" rel="noopener noreferrer">${match}</a>`;
    const shortenedUrlPathMatch = match.match(SHORTENED_URL_PATTERN)?.[1]?.toLowerCase();

    // If we have a shortenedUrl, and we loaded the visitDetails for it, we'll add extra text
    if (shortenedUrlPathMatch && urlVisitDetails) {
      const visitDetails = urlVisitDetails[shortenedUrlPathMatch];

      if (visitDetails) {
        const clicks = `<span class="typo-subtitle"> (${visitDetails.numVisits || 0} click${
          visitDetails.numVisits === 1 ? '' : 's'
        })</span>`;
        link = `${link}${clicks}`;
      }
    }

    return link;
  };

  // if we are including html, strip anything besides newlines. Later we add the <br /> tags back in to properly render the preview
  const htmlMessage =
    includesHTML && shouldTrim ? CkEditorNewlineTrimmer.trimMessage(messageBody) : messageBody;
  const messageWithHyperlinks = htmlMessage.replace(URL_PATTERN, wrapUrlLink);

  return includesHTML || messageBody !== messageWithHyperlinks ? (
    <div
      className={className || ''}
      dangerouslySetInnerHTML={{
        __html: shouldTrim
          ? CkEditorNewlineTrimmer.untrimMessage(messageWithHyperlinks)
          : messageWithHyperlinks,
      }}
      data-cy={dataCy}
    />
  ) : (
    <div className={className || ''} data-cy={dataCy}>
      {messageBody}
    </div>
  );
};
